import { useState, useEffect, useCallback } from 'react';
import { Article, ArticlesFilters, CreateArticleData, UpdateArticleData } from '../services/articleService';
import { articleService } from '../services/articleService';
import { toast } from 'react-hot-toast';
import { useApp } from '../context/AppContext';

export const useArticles = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const { isInitialized, initializeApp } = useApp();

  const fetchArticles = useCallback(async (filters?: ArticlesFilters) => {
    try {
      setLoading(true);
      const currentPage = filters?.page || 1;
      const currentLimit = filters?.limit || 6;
      
      console.log('Загрузка статей с параметрами:', {
        page: currentPage,
        limit: currentLimit,
        filters
      });

      const data = await articleService.getArticles({
        ...filters,
        page: currentPage,
        limit: currentLimit,
      });

      setArticles(data.articles);
      setTotal(data.total);
      setPage(currentPage);
      setError(null);
      
      if (!isInitialized) {
        await initializeApp();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке статей');
    } finally {
      setLoading(false);
    }
  }, [isInitialized, initializeApp]);

  // Создание новой статьи
  const createArticle = async (formData: FormData) => {
    try {
      const newArticle = await articleService.createArticle(formData);
      setArticles(prev => [...prev, newArticle]);
      toast.success('Статья успешно создана');
      return newArticle;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ошибка при создании статьи');
      throw error;
    }
  };

  // Обновление статьи
  const updateArticle = async (id: string, formData: FormData) => {
    try {
      const updatedArticle = await articleService.updateArticle(id, formData);
      setArticles(prev => prev.map(article => article.id === id ? updatedArticle : article));
      toast.success('Статья успешно обновлена');
      return updatedArticle;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ошибка при обновлении статьи');
      throw error;
    }
  };

  // Удаление статьи
  const deleteArticle = async (id: string) => {
    try {
      await articleService.deleteArticle(id);
      setArticles(prev => prev.filter(article => article.id !== id));
      toast.success('Статья успешно удалена');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при удалении статьи');
      toast.error('Ошибка при удалении статьи');
      throw err;
    }
  };

  // Публикация статьи
  const publishArticle = async (id: string) => {
    try {
      const publishedArticle = await articleService.publishArticle(id);
      setArticles(prev => prev.map(article => article.id === id ? publishedArticle : article));
      toast.success('Статья успешно опубликована');
      return publishedArticle;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при публикации статьи');
      toast.error('Ошибка при публикации статьи');
      throw err;
    }
  };

  // Загрузка статей при монтировании компонента
  useEffect(() => {
    fetchArticles({ page: 1, limit: 6 });
  }, [fetchArticles]);

  return {
    articles,
    loading,
    error,
    total,
    page,
    limit,
    setPage,
    setLimit,
    fetchArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    publishArticle,
  };
}; 